/** @fileoverview Shared transient notifications mounted by ToastProvider. Callers own message content and lifetime; each compact notification owns its live-region presentation and dismissal timer, paused while hovered or focused. Notifications never acknowledge durable business completion. */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from './AppIcon';
import './AppControls.css';

const STATUS_ICONS = {
  success: 'pi pi-check',
  error: 'pi pi-exclamation-circle',
  warn: 'pi pi-exclamation-triangle',
  info: 'pi pi-info-circle',
};

function ToastMessage({ message, onDismiss }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remaining = useRef(message.life ?? 3000);

  useEffect(() => {
    if (message.sticky || hovered || focused) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => onDismiss(message.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [message, hovered, focused, onDismiss]);

  const severity = message.severity || 'info';
  return <section
    role={severity === 'error' ? 'alert' : 'status'}
    aria-atomic="true"
    className={`app-toast app-toast--${severity}${message.detail ? ' app-toast--with-detail' : ''}`}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    onFocus={() => setFocused(true)}
    onBlur={event => {
      if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
    }}
  >
    <span className="app-toast-status" aria-hidden="true">
      <AppIcon icon={STATUS_ICONS[severity] || STATUS_ICONS.info} size={14} weight="bold" />
    </span>
    <div className="app-toast-content">
      {message.summary && <strong className="app-toast-summary">{message.summary}</strong>}
      {message.detail && <div className="app-toast-detail">{message.detail}</div>}
    </div>
    <button className="app-toast-dismiss" type="button" aria-label="Dismiss notification" onClick={() => onDismiss(message.id)}>
      <AppIcon icon="pi pi-times" size={14} weight="regular" />
    </button>
  </section>;
}

export const Toast = forwardRef(function AppToast(_props, ref) {
  const [messages, setMessages] = useState([]);
  const sequence = useRef(0);
  const dismiss = useCallback(id => {
    setMessages(current => current.filter(message => message.id !== id));
  }, []);
  useImperativeHandle(ref, () => ({ show(message) {
    const id = ++sequence.current;
    setMessages(current => [...current, { ...message, id }]);
  } }));
  return createPortal(<div className="app-toast-stack">
    {messages.map(message => <ToastMessage key={message.id} message={message} onDismiss={dismiss} />)}
  </div>, document.body);
});
